/**
 * DomOverlay — shared infrastructure for DOM UI overlays that sit on top of
 * the Phaser canvas.
 *
 * Why DOM over canvas: for menus, card carousels, and text-heavy modals, the
 * browser's native layout engine is vastly more capable (and cheaper) than
 * re-implementing flexbox in Phaser Graphics. We keep the game board +
 * HUD in Phaser (60/120fps WebGL) and surface the rest as DOM.
 *
 * Usage:
 *   import { DomOverlayHost, DomOverlay } from '../ui/dom/DomOverlay';
 *   DomOverlayHost.install();        // once, from main.ts
 *   const overlay = new MyMenuOverlay();
 *   overlay.open();                  // fades in with spring
 *   overlay.on('play', () => ...);   // DOM → Phaser bridge
 *   overlay.close();
 */

import './dom-ui.css';

/**
 * Host container — one per page. Installed once from main.ts, then every
 * overlay instance attaches/detaches inside it.
 */
export class DomOverlayHost {
  private static rootEl: HTMLDivElement | null = null;

  static install(): HTMLDivElement {
    if (DomOverlayHost.rootEl) return DomOverlayHost.rootEl;

    const existing = document.getElementById('dom-ui-root') as HTMLDivElement | null;
    if (existing) {
      DomOverlayHost.rootEl = existing;
      return existing;
    }

    const root = document.createElement('div');
    root.id = 'dom-ui-root';
    document.body.appendChild(root);
    DomOverlayHost.rootEl = root;
    return root;
  }

  static root(): HTMLDivElement {
    return DomOverlayHost.install();
  }

  /** Tear every active overlay down — used during scene transitions. */
  static clear(): void {
    if (!DomOverlayHost.rootEl) return;
    DomOverlayHost.rootEl.innerHTML = '';
  }
}

/**
 * Minimal typed event emitter — Phaser has one, but we avoid importing Phaser
 * into the DOM layer so the DOM bundle can be tree-shaken independently.
 */
type Listener<T> = (payload: T) => void;

class TinyEmitter<EventMap extends Record<string, unknown>> {
  private listeners: { [K in keyof EventMap]?: Array<Listener<EventMap[K]>> } = {};

  on<K extends keyof EventMap>(event: K, cb: Listener<EventMap[K]>): () => void {
    (this.listeners[event] ??= []).push(cb);
    return () => this.off(event, cb);
  }

  off<K extends keyof EventMap>(event: K, cb: Listener<EventMap[K]>): void {
    const arr = this.listeners[event];
    if (!arr) return;
    const idx = arr.indexOf(cb);
    if (idx >= 0) arr.splice(idx, 1);
  }

  emit<K extends keyof EventMap>(event: K, payload: EventMap[K]): void {
    const arr = this.listeners[event];
    if (!arr) return;
    for (const cb of [...arr]) cb(payload);
  }

  clear(): void {
    this.listeners = {};
  }
}

/**
 * Base class for an overlay. Subclasses implement `render()` which returns
 * the top-level element and sets up event listeners.
 */
export abstract class DomOverlay<EventMap extends Record<string, unknown> = Record<string, never>> {
  protected rootEl: HTMLDivElement;
  private bus = new TinyEmitter<EventMap>();
  private opened = false;

  constructor() {
    this.rootEl = document.createElement('div');
    this.rootEl.className = 'dom-overlay';
  }

  protected abstract render(): void;

  open(): void {
    if (this.opened) return;
    this.render();
    DomOverlayHost.root().appendChild(this.rootEl);
    // Force a reflow so the transition runs from the pre-transform state
    void this.rootEl.offsetWidth;
    this.rootEl.classList.add('is-visible');
    this.opened = true;
  }

  close(): void {
    if (!this.opened) return;
    this.rootEl.classList.remove('is-visible');
    const cleanup = () => {
      this.rootEl.remove();
      this.bus.clear();
      this.opened = false;
    };
    // Match the longest transition duration in dom-ui.css (.dom-overlay -> 420ms)
    setTimeout(cleanup, 440);
  }

  isOpen(): boolean { return this.opened; }

  on<K extends keyof EventMap>(event: K, cb: Listener<EventMap[K]>): () => void {
    return this.bus.on(event, cb);
  }

  protected emit<K extends keyof EventMap>(event: K, payload: EventMap[K]): void {
    this.bus.emit(event, payload);
  }

  /** Convenience helper — create an element with class + optional text. */
  protected el<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className?: string,
    text?: string
  ): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }
}
